import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import {
  getLegalEntityAndSelectedBusinessLineAndShippingConditionCodes,
  OrderBuilderState,
} from '@ordersnproducts/modules/order-builder/reducers';
import { EMPTY, Observable, of } from 'rxjs';
import { ContactService, ILegalEntity, IContact } from '@cemex-core/cmx-api';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { LoadFailRolesAction, LoadRolesCompleteAction, ReceivingPersonRoleActionTypes } from '../actions/roles.actions';
import {
  LoadFailTelephoneCodeAction,
  LoadTelephoneCodeCompleteAction,
  ReceivingPersonTelephoneCodeActionTypes,
} from '../actions/telephone.actions';
import { CmxClientService, CmxHttpClient, getLegalEntity } from '@cemex/cmx-client-layout';
import { HttpParams } from '@angular/common/http';
import { IContactPersonRolesResponse } from '../models/contact-person-role.model';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ContactPersonApiService } from '@ordersnproducts/modules/order-builder/modules/location-specification/services/contact-person.api.service';
import { TranslateService } from '@ngx-translate/core';
import {
  ContactPerson,
  IContactPersonRequest,
} from '@ordersnproducts/modules/order-builder/modules/location-specification/models/order-management/contact-person.model';

import * as fromReceivingPersonsAction from '@ordersnproducts/modules/order-builder/modules/location-specification/actions/receiving-persons.action';

import {
  ADD_CONTACT,
  DELETE_CONTACT,
  ReceivingPersonsAddContactSuccessAction,
  DeleteContactFailureAction,
  DeleteContactSuccessAction,
  DELETE_CONTACT_SUCCESS,
  DELETE_CONTACT_FAILURE,
  ReceivingPersonsSetAction,
} from '@ordersnproducts/modules/shared/modules/receiving-person/redux/actions/receiving-person.actions';
import { getValidJobsiteIdAndCode } from '../selectors';
import { getReceivingPerson } from '@ordersnproducts/modules/order-builder/modules/location-specification/selectors';
import { getReceivingPersonEntities } from '@ordersnproducts/modules/shared/modules/receiving-person/redux/selectors/index';
import { getContactPersonsArray } from '@ordersnproducts/modules/new-modify-order/modules/new-modify-product-specification/selectors';
import {
  FetchContacts,
  SelectContact,
} from '@ordersnproducts/modules/new-modify-order/modules/new-modify-product-specification/actions/receiving-persons.action';
import { AddNewContact } from '@ordersnproducts/modules/new-modify-order/modules/new-modify-product-specification/actions/receiving-persons.action';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';
export interface CountriesResponse {
  countries: Countries[];
}

export interface Countries {
  countryCallingCode: string;
  countryCode: string;
  countryDesc: string;
  isoCountryId: number;
}

@Injectable()
export class NewReceivingPersonEffects {
  getRoles$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReceivingPersonRoleActionTypes.LOAD_ROLES),
      withLatestFrom(this.store.pipe(select(getLegalEntity))),
      switchMap(([_payload, legalEntity]) =>
        this.getRoles(legalEntity).pipe(
          map((response: IContactPersonRolesResponse) => new LoadRolesCompleteAction(response.contactPersonRoles)),
          catchError((error) => {
            return of(new LoadFailRolesAction());
          }),
        ),
      ),
    ),
  );

  getTelephoneCode$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReceivingPersonTelephoneCodeActionTypes.LOAD_TELEPHONE_CODE),
      withLatestFrom(this.store.pipe(select(getLegalEntity))),
      switchMap(([_payload, legalEntity]) =>
        this.getCountries().pipe(
          map((response) => {
            return new LoadTelephoneCodeCompleteAction(response.countries);
          }),
          catchError((error) => {
            return of(new LoadFailTelephoneCodeAction());
          }),
        ),
      ),
    ),
  );

  addContact$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ADD_CONTACT),
      withLatestFrom(
        this.store.pipe(select(getLegalEntityAndSelectedBusinessLineAndShippingConditionCodes)),
        this.store.pipe(select(getValidJobsiteIdAndCode)),
      ),
      switchMap(([{ payload }, legalEntityAndShippingConditions, jobsite]) => {
        const aux: IContactPersonRequest = {
          countryAreaCode: payload['contact']['countryAreaCode'],
          phone: payload['contact']['phone'],
          extension: payload['contact']['extension'],
          contactPersonRole: { contactPersonRoleId: payload['contact']['contactPersonRoleId'] },
          name: payload['contact']['name'],
          firstName: payload['contact']['firstName'],
          lastName: payload['contact']['lastName'],
          email: payload['contact']['email'],
          customer: {
            customerCode: legalEntityAndShippingConditions.customer.legalEntityTypeCode,
            customerId: legalEntityAndShippingConditions.customer.legalEntityId,
            customerXCode: legalEntityAndShippingConditions.customer.legalEntityTypeCode,
          },
          jobsite: {
            jobsiteId: jobsite.id,
            jobsiteCode: jobsite.code,
            jobsiteXCode: jobsite.code,
          },
          isPrimaryContact: false,
        };
        return this.contactPersonApi.postNewContact(aux).pipe(
          catchError((error) => {
            this.alertService.addMainAlert('danger', this.translate.instant('views.contact.add_contact_error'));
            return EMPTY;
          }),
          map((response: ContactPerson) => {
            const alertMessageKey = this.featureToggleService.isFeatureEnabled(Features.CLOUD_CONTACT_MODULE)
              ? 'views.contact.add_new_contact_success'
              : 'views.contact.add_contact_success';
            this.alertService.addMainAlert('success', this.translate.instant(alertMessageKey));
            this.store.dispatch(new fromReceivingPersonsAction.AddNewContact(response));
            this.store.dispatch(new fromReceivingPersonsAction.SelectContact(response.contactId));
            this.store.dispatch(new AddNewContact(response));
            this.store.dispatch(new SelectContact(response.contactId));
            return new ReceivingPersonsAddContactSuccessAction();
          }),
        );
      }),
    ),
  );

  deleteContact$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DELETE_CONTACT),
      withLatestFrom(
        this.store.pipe(select(getLegalEntityAndSelectedBusinessLineAndShippingConditionCodes)),
        this.store.pipe(select(getValidJobsiteIdAndCode)),
        this.store.pipe(select(getReceivingPerson)),
        // Contacts for (old) order-builder
        this.store.pipe(select(getReceivingPersonEntities)),
        this.store.pipe(select(getContactPersonsArray)),
      ),
      switchMap(
        ([
          { payload },
          legalEntityAndShippingConditions,
          jobsite,
          receivingPersons,
          personEntities,
          contactPersonArray,
        ]) => {
          const legalEntityId = `${legalEntityAndShippingConditions.customer.legalEntityId}.1`;
          const shipmentLocationId = `${jobsite.id}.2`;

          // bulk cement logic, if the contact is not in the receivingPersons, then it is in the contactPersonArray
          const contactSelectedForDeleteId = Number(payload['contactSelectedForDeleteId']);
          const selectedPerson = receivingPersons.entities[contactSelectedForDeleteId] as IContact;
          const selectedPersonEntity = personEntities.find((entity) => entity.contactId === contactSelectedForDeleteId);
          const contactPerson = contactPersonArray.find(
            (contact) => contact.contactId === contactSelectedForDeleteId,
          ) as IContact;
          let contactTypeCode = '';
          if (selectedPersonEntity && selectedPersonEntity.contactType) {
            contactTypeCode = selectedPersonEntity.contactType.contactTypeCode;
          } else if (selectedPerson && selectedPerson.contactType) {
            contactTypeCode = selectedPerson.contactType.contactTypeCode;
          } else if (contactPerson && contactPerson.contactType) {
            contactTypeCode = contactPerson.contactType.contactTypeCode;
          }
          return this.contactService
            .deleteContactPerson(
              payload['contactSelectedForDeleteId'],
              payload['reasonControl'],
              contactTypeCode,
              legalEntityId.toString(),
              shipmentLocationId.toString(),
            )
            .pipe(
              map(() => {
                return new DeleteContactSuccessAction();
              }),
              catchError(() => of(new DeleteContactFailureAction())),
            );
        },
      ),
    ),
  );

  deleteContactSuccessBulk$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DELETE_CONTACT_SUCCESS),
      map(() => {
        // bulk cement logic
        this.alertService.addMainAlert(
          'success',
          this.translate.instant('views.location.delete_contact_confimation_delete'),
        );
        return new fromReceivingPersonsAction.FetchContacts();
      }),
    ),
  );

  deleteContactSuccessRmx$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DELETE_CONTACT_SUCCESS),
      map(() => {
        return new FetchContacts();
      }),
    ),
  );

  deleteContactSuccessBulkRemoveSelect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DELETE_CONTACT_SUCCESS),
      map(() => {
        return new SelectContact(null);
      }),
    ),
  );

  deleteContactSuccessRmxRemoveSelect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DELETE_CONTACT_SUCCESS),
      map(() => {
        return new ReceivingPersonsSetAction(null);
      }),
    ),
  );

  deleteContactFail$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DELETE_CONTACT_FAILURE),
        tap(() => {
          this.alertService.addMainAlert(
            'danger',
            this.translate.instant('views.location.delete_contact_error_placing'),
          );
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private http: CmxHttpClient,
    private contactPersonApi: ContactPersonApiService,
    private alertService: CmxClientService,
    private translate: TranslateService,
    private contactService: ContactService,
    private featureToggleService: FeatureToggleService,
  ) {}

  getRoles(customer: ILegalEntity): Observable<IContactPersonRolesResponse> {
    let params = new HttpParams();
    params = params.set('countryCode', customer.countryCode.trim());
    return this.http.get(`v3/im/contactpersonroles`, { params });
  }

  getCountries(): Observable<CountriesResponse> {
    return this.http.get(`v2/im/countries`, {});
  }
}
